import { levels } from 'pino';

import type * as types from '../types';

import { LogInternally } from '../LogInternally';

export interface StrategyOptions {
  readonly isEnabled?: boolean;
}

/**
 * An abstract class that should be extended by all strategy implementations for the logging
 * system.  Strategies are responsible for handling the emission of log messages, {@link string},
 * log context, {@link types.LogContext}, and sometimes errors, {@link types.LoggerCustomError}.
 *
 * @see {types.ILoggerStrategy}
 */
export abstract class Strategy<O extends StrategyOptions = StrategyOptions>
  extends LogInternally
  implements types.IStrategy
{
  protected readonly options: O;
  public abstract strategyId: types.StrategyId;

  constructor(options: O) {
    super();
    this.options = options;
  }

  protected isLogLevelSatisfied(logger: types.ILoggerConfiguration, level: types.LogLevel) {
    return levels.values[logger.level] <= levels.values[level];
  }

  protected abstract log(
    logger: types.ILoggerConfiguration,
    level: types.LogLevel,
    params: { readonly time?: number } & types.ParsedArgs,
  ): void;

  /**
   * Logs the provided information via the specific abstract 'log' method attached to the strategy,
   * {@link Strategy}.
   *
   * This method should not be overridden by subclasses, instead - the abstract 'log' method should
   * be implemented by subclasses of the {@link Strategy} class.
   *
   * @param {types.LogLevel} level
   *   The log level associated with the original method on the {@link Logger} that was invoked.
   * @param {{ readonly time?: number } & types.ParsedArgs} params
   *   Information that should be included in the log issued by the underlying {@link Strategy}.
   */
  public logWithStrategy(
    logger: types.ILoggerConfiguration,
    level: types.LogLevel,
    params: { readonly time?: number } & types.ParsedArgs,
  ) {
    if (this.isEnabled && this.isLogLevelSatisfied(logger, level) && logger.isEnabled) {
      this.log(logger, level, params);
    }
  }

  protected get internalMessagePrefix() {
    return this.strategyId;
  }

  /**
   * Whether or not the given strategy, {@link types.IStrategy}, is enabled.  If the strategy,
   * {@link types.IStrategy}, is explicitly disabled, and the logger instance,
   * {@link types.ILogger}, that the strategy is bound to is enabled, then the strategy will still
   * not emit any log messages.
   */
  protected get isEnabled() {
    return this.options.isEnabled ?? true;
  }
}
